/**
 * "Preview the first frame" for the AI video ad generator (tool id
 * song-promo-ad), Oct 27 2026, from Zeely's video ad builder: a cheap still
 * of the presenter holding the product, made BEFORE the video. The user sees
 * it and keeps it (or tries again), and the video then renders from that
 * frame (image to video) instead of going straight from the product photo and
 * the presenter reference to a clip. The point is catching a wrong product or
 * person for 9 credits instead of after a 90-220 credit render.
 *
 * A frame is its own creative tool job (tool id "ad-start-frame"), like a
 * finished ad (shared/creativeAdFinish.ts): history, refunds, the active-job
 * limit and the account-deletion sweep reuse the creative-tool plumbing. It is
 * charged by creativeTools.adStartFrame (server/creativeAdStartFrameRouter.ts)
 * and run by the job loop through buildCreativeRequest; the ad's own job then
 * carries the frame as `startFrame` next to its product photo, so the Finish
 * ad end card still shows the product.
 */
import type { CreativeTool } from "./creativeTools";

export const AD_START_FRAME_TOOL_ID = "ad-start-frame";
/**
 * The engine a frame records (creative_tool_jobs.model): Nano Banana 2.1 on
 * Solmi's Gemini key, with Seedream 5.0 Lite edit on WaveSpeed as the
 * fallback (server/creativeToolProvider.ts). Frames made before Nov 2 2026
 * record "seedream".
 *
 * Oct 27 2026 probe, two of the ad templates' product + presenter pairs
 * (skincare jar + Emma, headphones + Kenji): Seedream and Nano Banana Pro
 * both kept the face and the product, label and all. Seedream took ~55 s at
 * $0.035 a frame (WaveSpeed balance delta $0.070 for two); Nano Banana Pro
 * ~19 s at ~$0.14 (1,120 image tokens). The Oct 23 likeness study also had
 * Seedream ahead of the Google models on faces
 * (reference_lookalike_face_ab_test_oct23).
 *
 * Nov 2 2026 (Kitty: "use nb 2.1"): Nano Banana 2.1 first, $0.0336 a 1K
 * frame at list plus its input tokens. It also draws the frame the
 * Standard tier's Veo 3.1 Lite starts from when the user kept none
 * (creativeVertexVeo.ts).
 */
export const AD_START_FRAME_MODEL = "nano-banana-2.1";
/**
 * Credits for one frame: the image editor's Standard edit price
 * (creativeModels, a test keeps them equal). ~$0.16 at a typical $0.0175 a
 * credit against ~$0.04 to us; the Seedream fallback (~$0.035) the same.
 */
export const AD_START_FRAME_CREDITS = 9;
/** How much of the ad's direction the frame's prompt quotes. */
export const AD_START_FRAME_DIRECTION_MAX = 1500;

/**
 * The frame's size per ad shape on Seedream (width*height). The frame is
 * made in the video's shape: image to video follows its first frame's shape
 * (the Standard engine takes no shape of its own).
 */
export const AD_START_FRAME_SIZES: Readonly<Record<string, string>> = {
  "9:16": "1440*2560",
  "16:9": "2560*1440",
  "1:1": "2048*2048",
  "4:3": "2304*1728",
  "3:4": "1728*2304",
  "21:9": "3024*1296",
};

/**
 * The job's tool entry, so a frame plays and refunds like any other creative
 * result (findCreativeToolForJob). Not in creativeTools: it has no page, the
 * generic create refuses it, and the ad studio shows frames in the composer
 * rather than in the results grid (creativeTools.list leaves them out).
 */
export const adStartFrameTool: CreativeTool = {
  id: AD_START_FRAME_TOOL_ID,
  name: "Ad first frame",
  description: "The opening frame of an AI video ad: the presenter with the product.",
  category: "image",
  // The product photo, then the presenter (absent: Auto, an invented one).
  fields: ["image", "reference"],
  optionalFields: ["reference"],
  models: [AD_START_FRAME_MODEL, "seedream"],
};

/**
 * The opening moment per ad style: where the presenter is, what they do with
 * the product and where they look. The product is always in view, because
 * the video only sees this frame: a product that is not in it is unknown to
 * the engine. Try-on styles wear it instead of holding it. Styles added later
 * get the default.
 */
const FRAME_MOMENTS: Record<string, string> = {
  ugc: "holds the product up beside their face toward the phone camera, in a real, lived-in bedroom or living room, looking into the camera with a relaxed smile",
  "how-to-demo": "holds the product out toward the camera, ready to show how it is used, in a clean, bright room where it would be used, looking into the camera",
  unboxing: "holds the product they just lifted out of its open box, the box on the table in front of them at home, looking into the camera with real excitement",
  "product-review": "holds the product up and looks at it for the first time, curious, in a cozy home setting, then back toward the camera",
  "cinematic-ad": "holds the product in a cinematic, professionally lit scene with dramatic light, looking into the camera with quiet confidence",
  "ugc-virtual-try-on": "wears or has just applied the product, taking a casual mirror selfie in a bedroom, smiling at the result",
  "pro-virtual-try-on": "wears the product, posed in a clean photo studio with soft even light, looking into the camera",
  "before-after": "holds the product up at home, in daylight, looking into the camera with a pleased smile",
  delivery: "holds the product they just took out of a parcel, the open box on a table at home, looking into the camera with real excitement",
  "street-interview": "stands on a busy city sidewalk in daylight holding the product, a plain handheld microphone held toward them from just off camera, looking at the interviewer",
  podcast: "sits at a desk in a cozy podcast studio with headphones on and a big microphone on a boom arm in front of them, holding the product up toward the camera",
  "hidden-camera": "holds the product in an ordinary living room, seen from a camera hidden high in a corner with a slight fisheye, glancing up at it with a guilty grin",
};
const DEFAULT_MOMENT =
  "holds the product up toward the camera in a real, natural setting, looking into the camera";

/** Framing per shape: a vertical ad is waist-up, a wide one a medium shot. */
const framing = (aspectRatio: string, adStyle: string | undefined) => {
  if (adStyle === "pro-virtual-try-on") return "Three-quarter-length framing so the product is fully visible";
  const [w, h] = aspectRatio.split(":").map(Number);
  return w < h
    ? "Vertical, waist-up framing with a little room above the head"
    : w > h
      ? "Wide, medium framing with the presenter off-center"
      : "Square, waist-up framing";
};

/**
 * The frame's image prompt. The images go in as [product, presenter], so the
 * prompt names them first and second; without a presenter (Auto) the engine
 * invents one. A direction (typed, from a template, or a written script)
 * sets the scene, but only its opening moment is drawn and spoken lines are
 * not.
 */
export function adStartFramePrompt({
  adStyle,
  direction = "",
  aspectRatio,
  hasPresenter,
}: {
  adStyle?: string;
  direction?: string;
  aspectRatio: string;
  hasPresenter: boolean;
}): string {
  const moment = (adStyle && FRAME_MOMENTS[adStyle]) || DEFAULT_MOMENT;
  const scene = direction.replace(/\s+/g, " ").trim().slice(0, AD_START_FRAME_DIRECTION_MAX);
  return [
    "The opening frame of a short video ad, looking like a real photo, not an illustration.",
    hasPresenter
      ? "The person is the presenter in the second image: keep their face, hair, skin tone and build exactly the same."
      : "The person is an invented, ordinary-looking adult presenter who suits the product, not a real or famous person.",
    `The product is the one in the ${hasPresenter ? "first " : ""}image: keep its shape, colors, label and logo exactly as shown, at a believable real-world size, with its front clearly visible.`,
    `The presenter ${moment}.`,
    `${framing(aspectRatio, adStyle)}, natural light, sharp focus on the face and the product, mouth closed.`,
    ...(scene
      ? [
          `Follow the ad's direction for the setting, wardrobe and light, but draw only its first moment with the product in view, and ignore any spoken lines: """${scene}"""`,
        ]
      : []),
    "Add no text, captions, subtitles, logos or watermarks beyond what is printed on the product itself.",
  ].join(" ");
}

/**
 * The prompt that fits a photo to the ad's shape before Veo 3.1 Lite (the
 * Standard tier) animates it. Veo pads a photo of another shape with black
 * bars (Nov 2 2026 live run: a square sneaker photo came back letterboxed in
 * 9:16), so Nano Banana 2.1 extends the photo to the shape first
 * (creativeVertexVeo.ts). A photo already in the ad's shape goes as it is.
 */
export function adFitFramePrompt(aspectRatio: string): string {
  const [w, h] = aspectRatio.split(":").map(Number);
  const shape = w < h ? "a vertical" : w > h ? "a wide" : "a square";
  return [
    `Extend this photo to fill ${shape} ${aspectRatio} frame: the opening frame of a short video ad.`,
    "Keep everything already in the photo exactly as it is: the product's shape, colors, label and logo, its size, and the light.",
    "Continue the photo's own background, surface and lighting naturally into the new space, with no borders, bars or blur.",
    "Add no text, captions, logos or watermarks beyond what is printed on the product itself.",
  ].join(" ");
}

/**
 * The line that leads an ad's video prompt when it renders from a frame: the
 * frame is the first image the engine sees, so the presenter and product are
 * held to it.
 */
export const AD_START_FRAME_VIDEO_LEAD =
  "The video starts on this exact frame: the person in it is the presenter and the product is the one they have, so keep the presenter's face and the product's shape, colors, label and logo exactly as they are in the frame. ";
